import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import AuthorizedAction, {
  isUnAuthorized,
} from '../../components/AuthorizedAction.js';
import OrganizationHeader from '../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import UserForm from '../../components/organizations/Users/UserForm.jsx';
import UserSubHeaderInfo from '../../components/organizations/Users/UserSubHeaderInfo.jsx';
import { isBase64Image, mapFinalFormErrors } from '../../lib/utils.js';
import {
  retrieveUser as retrieveCurrentUser,
  selectCurrentOrganizationId,
  selectUser as selectCurrentUser,
  selectUserRestrictions,
} from '../../modules/auth.js';
import { updateNotificationState } from '../../modules/notifications.js';

import {
  createUser,
  deleteUser,
  retrieveUser,
  selectUser,
  updateUser,
} from '../../modules/users.js';

import SubHeader from '../../components/SubHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state, ownProps) => ({
  organizationId: selectCurrentOrganizationId(state),
  currentUser: selectCurrentUser(state),
  user: selectUser(ownProps.userId)(state),
  editing: !!ownProps.userId,
  restrictions: selectUserRestrictions(state),
});

const mapDispatchToProps = {
  createUser,
  retrieveUser,
  updateUser,
  deleteUser,
  retrieveCurrentUser,
  updateNotificationState,
};

const UserFormContainer = (props) => {
  const {
    slug,
    createUser,
    retrieveCurrentUser,
    updateUser,
    retrieveUser,
    organizationId,
    userId,
    user,
    editing,
    currentUser,
    deleteUser,
    updateNotificationState,
    restrictions,
  } = props;

  const [selectedImage, setSelectedImage] = useState('');
  const [imageSize, setImageSize] = useState('');

  const isBlockedDeletedUser = isUnAuthorized(
    'destroy',
    'userOrganizationRole',
    restrictions
  );

  const redirectToUser = (id) =>
    pushRoute('organization/users/show', {
      userId: String(id),
    });

  const updateUserHeaders = (email) => {
    // No-op: Token remains valid regardless of email change
    // Backend validates the token, not the email header
  };

  const notify = () => {
    updateNotificationState({
      isShown: true,
      type: 'success',
      forPath: '/organization/users/show',
      message:
        'Permissions successfully added. The user has received an invitation to join' +
        ' ChangeSync. User will remain in "Invited" status until they log in.',
    });
  };

  const onSubmit = (values) => {
    const resource = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      orgRole: values.orgRole,
      organizationId,
    };

    selectedImage ? (resource.avatar = selectedImage) : null;
    values.password ? (resource.password = values.password) : null;
    values.currentPassword
      ? (resource.currentPassword = values.currentPassword)
      : null;

    const submitPromise = editing
      ? updateUser(userId, resource)
      : createUser(resource);

    return submitPromise
      .then(({ result }) => {
        !editing && values.orgRole && notify();

        if (userId === currentUser.id) {
          values.email && updateUserHeaders(values.email);

          retrieveCurrentUser({ organization_slug: slug }).then(() =>
            redirectToUser(result)
          );
        } else redirectToUser(result);
      })
      .catch(mapErrors);
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    const base64 = await convertToBase64(file);

    setSelectedImage(base64);
    setImageSize(file.size);
  };

  const onDelete = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      const submitPromise = deleteUser(userId);

      submitPromise
        .then(() => pushRoute('organization/users'))
        .catch(mapErrors);
    }
  };

  const validate = (values) => {
    const errors = {};

    if (!values.firstName) {
      errors.firstName = 'First name is required';
    }

    if (!values.lastName) {
      errors.lastName = 'Last name is required';
    }

    if (!values.email) {
      errors.email = 'Email is required';
    }

    if (values.admin && !values.orgRole) {
      errors.orgRole = 'Role is required';
    }

    if (values.password) {
      if (userId === currentUser.id && !values.currentPassword) {
        errors.currentPassword = 'Old password is required';
      }

      if (values.password !== values.passwordConfirmation) {
        errors.passwordConfirmation = 'Passwords must match';
      } else {
        delete errors.passwordConfirmation;
      }
    }

    if (selectedImage && imageSize) {
      if (!isBase64Image(selectedImage)) {
        errors.avatar = 'Allowed formats: .jpeg, .png, .jpg';
      }
    }

    if (selectedImage && imageSize > 10485760) {
      errors.avatar = 'Max file size: 10Mb';
    }

    return errors;
  };

  useEffect(() => {
    editing && retrieveUser(userId);
  }, []);

  return (
    <React.Fragment>
      <OrganizationHeader
        slug={slug}
        userId={userId}
        userName={user?.fullName || ''}
      />

      <SubHeader flexJustify small>
        {editing && user ? (
          <React.Fragment>
            <UserSubHeaderInfo user={user} />

            {currentUser.id !== userId &&
            (!isBlockedDeletedUser || user.orgRole !== 'admin') ? (
              <AuthorizedAction action="delete" subject="user">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => onDelete(userId)}
                >
                  Delete
                </button>
              </AuthorizedAction>
            ) : null}
          </React.Fragment>
        ) : (
          <h2>Add User</h2>
        )}
      </SubHeader>

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  <Form
                    slug={slug}
                    component={UserForm}
                    onSubmit={onSubmit}
                    validate={validate}
                    handleImageSelect={handleImageSelect}
                    initialValues={editing ? user : {}}
                    editing={editing}
                    userId={userId}
                    currentUserId={currentUser.id}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UserFormContainer);
